import { FC, Suspense } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useAuthorizationService } from 'src/apis/authorization-service/use-authorization-service';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { UserMenu } from 'src/components/layout/header/user-menu';
import {
  LayoutContainer,
  LayoutContent,
  LayoutSidebar,
  Sidebar,
} from 'src/components/layout/sidebar';
import { PageSpinner } from 'src/components/spinner-container';
import { Sticky } from 'src/components/sticky';
import { Platform } from 'src/pages/platform';
import { DirectFcfsSettings } from 'src/pages/settings/direct-fcfs';
import { NotificationSettings } from 'src/pages/settings/notifications';
import { OrganisationManagement } from 'src/pages/settings/organisation';
import { useOrganisationService } from 'src/pages/settings/organisation/use-organisation-service';
import { PermissionGroups } from 'src/pages/settings/permission-groups';
import { PremiumServices } from 'src/pages/settings/premium-services';
import { Profile } from 'src/pages/settings/profile';
import { AssignmentRegistrationSettings } from 'src/pages/settings/registration';

export const Settings: FC = () => {
  const authorizationService = useAuthorizationService();
  const organisationService = useOrganisationService();

  if (authorizationService?.response === null || !organisationService.response)
    return <PageSpinner />;

  return (
    <LayoutContainer>
      <LayoutSidebar>
        <Sticky>
          <Sidebar>
            <UserMenu
              authorizationService={authorizationService?.response.data ?? null}
              organisationService={organisationService.response.data}
              show={{
                personalSettings: true,
                companySettings: true,
                transportSettings: true,
                storageSettings: true,
                shipperSettings: true,
                otherSettings: false,
              }}
            />
          </Sidebar>
        </Sticky>
      </LayoutSidebar>

      <LayoutContent>
        <Suspense fallback={<PageSpinner />}>
          <Switch>
            <Route path="/settings/profile" exact>
              <Profile />
            </Route>

            <Route path="/settings/notifications">
              <NotificationSettings />
            </Route>

            <Route path="/settings/organisation">
              <OrganisationManagement />
            </Route>

            <Route path="/settings/permission-groups">
              <PermissionGroups />
            </Route>

            <Route path="/settings/assignment-registration">
              <AssignmentRegistrationSettings />
            </Route>

            <Route path="/settings/platform" exact>
              <Platform />
            </Route>

            <Route path="/settings/direct-fcfs" exact>
              <DirectFcfsSettings />
            </Route>

            <Route path="/settings/premium-services" exact>
              <PremiumServices />
            </Route>

            <Route>
              <ThrowNotFound />
            </Route>
          </Switch>
        </Suspense>
      </LayoutContent>
    </LayoutContainer>
  );
};

export const ProfileSettingsRedirect: FC = () => {
  const { pathname, search, state, hash } = useLocation();
  return (
    <Redirect
      to={{
        pathname: pathname.replace('/profile-settings', '/settings'),
        search,
        state,
        hash,
      }}
    />
  );
};
